import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Tabs } from 'antd';
import type { MenuProps } from 'antd';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    Button,
    Col,
    FileTextOutlinedIcon,
    Header as ServaHeader,
    LogoutIcon,
    Menu,
    Modal as ServaModal,
    Option,
    RefreshIcon,
    Row,
    Select,
    Skeleton,
    Space,
    SettingsIcon,
    Text,
    Title,
    Tooltip,
    UserIcon,
} from '@servahealth/serva-ui-kit';
import buildInfo from '../../buildInfo';
import useAuth from '../../Hooks/useAuth';
import logo from '../../Images/BIUILogo.png';
import Maintenance from '../../Constants/Endpoints/Maintenance';
import PropertyEntryResponseModel from '../../Interfaces/Responses/Maintenance/PropertyEntryResponseModel';
import NLogEntryModelResponse from '../../Interfaces/Responses/Maintenance/NLogEntryModelResponse';
import AccountImpersonateUser from './AccountImpersonateUser';
import AccountUserLogs from './AccountUserLogs';
import {
    canImpersonateUser,
    canManageLinkages,
    canManageTechAdministration,
    canManageUsers,
    canUseBIMaintenance,
    canUseAdHocReportBuilder,
    canViewDiagnosticInformation as userCanViewDiagnosticInformation,
} from './AuthCapabilityHelper';

const AppHeader: React.FC = () => {
    const { user, onImpersonateSelected, logout } = useAuth();
    const nav = useNavigate();

    const [accountUserLogsVisible, setAccountUserLogsVisible] = useState(false);
    const [accountImpersonateUserVisible, setAccountImpersonateUserVisible] = useState(false);
    const [selectedImpersonatable, setSelectedImpersonatable] = useState('');

    const [diagnosticInformationVisible, setDiagnosticInformationVisible] = useState(false);
    const [diagnosticInformationActiveTab, setDiagnosticInformationActiveTab] = useState('frontend-application');

    const [diagnosticLogLevel, setDiagnosticLogLevel] = useState('E');
    const [diagnosticLogRowLimit, setDiagnosticLogRowLimit] = useState('100');
    const [appliedDiagnosticLogLevel, setAppliedDiagnosticLogLevel] = useState('E');
    const [appliedDiagnosticLogRowLimit, setAppliedDiagnosticLogRowLimit] = useState('100');
    const [aiLogLevel, setAILogLevel] = useState('*');
    const [aiLogRowLimit, setAILogRowLimit] = useState('100');
    const [appliedAILogLevel, setAppliedAILogLevel] = useState('*');
    const [appliedAILogRowLimit, setAppliedAILogRowLimit] = useState('100');

    const canViewDiagnosticInformation = userCanViewDiagnosticInformation(user);

    const buildFrontendVersion = () => {
        const buildDate = new Date(buildInfo.buildDate);
        const buildYear = buildDate.getFullYear();
        const buildStart = new Date(buildYear, 0, 0);
        const buildDOY = Math.floor((buildDate.getTime() - buildStart.getTime()) / 86400000);

        return 'B:' + buildInfo.buildVersion + ' D:' + buildYear + '-' + buildDOY;
    };

    const buildDiagnosticRows = () => {
        const frontendRows = [
            {
                propertyCategory: 'FRONTEND APPLICATION',
                propertyName: 'Version',
                propertyValue: buildFrontendVersion(),
            },
            {
                propertyCategory: 'FRONTEND APPLICATION',
                propertyName: 'Origin',
                propertyValue: window.location.origin,
            },
            {
                propertyCategory: 'FRONTEND APPLICATION',
                propertyName: 'Host Name',
                propertyValue: window.location.hostname,
            },
            {
                propertyCategory: 'FRONTEND APPLICATION',
                propertyName: 'Port',
                propertyValue: window.location.port || '[Default]',
            },
            {
                propertyCategory: 'FRONTEND APPLICATION',
                propertyName: 'Protocol',
                propertyValue: window.location.protocol,
            },
        ];

        const backendRows = diagnosticProperties?.dataSets ?? [];

        return [...frontendRows, ...backendRows];
    };

    const buildDiagnosticCategoryGroups = () => {
        return buildDiagnosticRows().reduce<Record<string, ReturnType<typeof buildDiagnosticRows>>>((groups, eachPropertyEntry) => {
            const propertyCategory = eachPropertyEntry.propertyCategory || 'BACKEND APPLICATION';

            return {
                ...groups,
                [propertyCategory]: [
                    ...(groups[propertyCategory] ?? []),
                    eachPropertyEntry,
                ],
            };
        }, {});
    };

    const renderDiagnosticPropertyRows = (propertyCategory: string) => {
        const propertyEntries = buildDiagnosticCategoryGroups()[propertyCategory] ?? [];

        return (
            <div style={{display: 'grid', gridTemplateColumns: '220px 1fr', gap: '8px 16px', alignItems: 'center'}}>
                {propertyEntries.map((eachPropertyEntry) => (
                    <React.Fragment key={`${propertyCategory}-${eachPropertyEntry.propertyName}`}>
                        <Text strong={true}>{eachPropertyEntry.propertyName}</Text>
                        <Text>{eachPropertyEntry.propertyValue}</Text>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderDiagnosticLogs = () => {
        return (
            <>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                        <Text style={{paddingTop: '4px'}}>Entry Level:</Text>
                    </Col>
                    <Col span={5}>
                        <Select
                            style={{paddingTop: '4px', width: '100%'}}
                            value={diagnosticLogLevel}
                            onChange={(value: string) => setDiagnosticLogLevel(value)}
                        >
                            <Option value='E'>Error</Option>
                            <Option value='D'>Debug</Option>
                            <Option value='I'>Info</Option>
                        </Select>
                    </Col>
                    <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                        <Text style={{paddingTop: '4px'}}>Rows:</Text>
                    </Col>
                    <Col span={5}>
                        <Select
                            style={{paddingTop: '4px', width: '100%'}}
                            value={diagnosticLogRowLimit}
                            onChange={(value: string) => setDiagnosticLogRowLimit(value)}
                        >
                            <Option value='100'>100</Option>
                            <Option value='200'>200</Option>
                            <Option value='400'>400</Option>
                            <Option value='800'>800</Option>
                        </Select>
                    </Col>
                    <Col span={8}/>
                </Row>
                <div style={{overflow: 'auto', height: '420px', marginTop: '12px', whiteSpace: 'nowrap'}}>
                    {
                        isGetDiagnosticLogsLoading
                        ?
                        <Skeleton paragraph={{ rows: 12 }} active />
                        :
                        isGetDiagnosticLogsError
                        ?
                        <Text style={{color: 'red'}}>Unable to load diagnostic logs.</Text>
                        :
                        diagnosticLogs?.dataSets.map((eachLog, index) => {
                            const entryMessage = eachLog.entryMessage.trim();
                            const entryDate = eachLog.entryDate.includes('.') ? eachLog.entryDate.substring(0, eachLog.entryDate.indexOf('.')) : eachLog.entryDate;
                            const displayedEntryMessage = entryMessage.length > 1500 ? entryMessage.substring(0, 1500) : entryMessage;
                            const entryParams = eachLog.entryParams.trim();
                            const entryException = eachLog.entryException.trim();
                            const trailingDetails = `${entryParams}${entryException ? ` ${entryException}` : ''}`;

                            return (
                                <div
                                    key={`${eachLog.entryDate}-${eachLog.entryLevel}-${index}`}
                                    style={{whiteSpace: 'nowrap', fontFamily: 'Consolas, "Courier New", monospace', fontSize: '14px', lineHeight: '18px'}}
                                >
                                    {`${entryDate} - ${eachLog.entryIPAddress} - ${eachLog.entryLevel} - [${displayedEntryMessage}] ${trailingDetails}`}
                                </div>
                            );
                        })
                    }
                </div>
            </>
        );
    };

    const renderAILogs = () => {
        return (
            <>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                        <Text style={{paddingTop: '4px'}}>Entry Level:</Text>
                    </Col>
                    <Col span={7}>
                        <Select
                            style={{paddingTop: '4px', width: '100%'}}
                            value={aiLogLevel}
                            onChange={(value: string) => setAILogLevel(value)}
                        >
                            <Option value='*'>All</Option>
                            <Option value='AI.ProviderSystem'>Provider System</Option>
                            <Option value='AI.ReportPageLayoutAssist'>Report Page Layout Assist</Option>
                            <Option value='AI.ReportExplain'>Report Explain</Option>
                            <Option value='AI.DatasetExplain'>Dataset Explain</Option>
                            <Option value='AI.ChartExplain'>Chart Explain</Option>
                            <Option value='AI.FilterAssist'>Filter Assist</Option>
                            <Option value='AI.SqlReview'>SQL Review</Option>
                            <Option value='AI.SecurityReview'>Security Review</Option>
                        </Select>
                    </Col>
                    <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                        <Text style={{paddingTop: '4px'}}>Rows:</Text>
                    </Col>
                    <Col span={5}>
                        <Select
                            style={{paddingTop: '4px', width: '100%'}}
                            value={aiLogRowLimit}
                            onChange={(value: string) => setAILogRowLimit(value)}
                        >
                            <Option value='100'>100</Option>
                            <Option value='200'>200</Option>
                            <Option value='400'>400</Option>
                            <Option value='800'>800</Option>
                        </Select>
                    </Col>
                    <Col span={6}/>
                </Row>
                <div style={{overflow: 'auto', height: '420px', marginTop: '12px', whiteSpace: 'nowrap'}}>
                    {
                        isGetAILogsLoading
                        ?
                        <Skeleton paragraph={{ rows: 12 }} active />
                        :
                        isGetAILogsError
                        ?
                        <Text style={{color: 'red'}}>Unable to load AI logs.</Text>
                        :
                        aiLogs?.dataSets.map((eachLog, index) => {
                            const entryMessage = eachLog.entryMessage.trim();
                            const entryDate = eachLog.entryDate.includes('.') ? eachLog.entryDate.substring(0, eachLog.entryDate.indexOf('.')) : eachLog.entryDate;
                            const displayedEntryMessage = entryMessage.length > 1500 ? entryMessage.substring(0, 1500) : entryMessage;
                            const entryParams = eachLog.entryParams.trim();
                            const entryException = eachLog.entryException.trim();
                            const trailingDetails = `${entryParams}${entryException ? ` ${entryException}` : ''}`;

                            return (
                                <div
                                    key={`${eachLog.entryDate}-${eachLog.entryLevel}-${index}`}
                                    style={{whiteSpace: 'nowrap', fontFamily: 'Consolas, "Courier New", monospace', fontSize: '14px', lineHeight: '18px'}}
                                >
                                    {`${entryDate} - ${eachLog.entryIPAddress} - ${eachLog.entryLevel} - [${displayedEntryMessage}] ${trailingDetails}`}
                                </div>
                            );
                        })
                    }
                </div>
            </>
        );
    };

    const renderDiagnosticTabs = () => {
        return (
            <Tabs
                activeKey={diagnosticInformationActiveTab}
                onChange={setDiagnosticInformationActiveTab}
                items={[
                    {
                        key: 'frontend-application',
                        label: 'Frontend Application',
                        children: renderDiagnosticPropertyRows('FRONTEND APPLICATION'),
                    },
                    {
                        key: 'backend-application',
                        label: 'Backend Application',
                        children: renderDiagnosticPropertyRows('BACKEND APPLICATION'),
                    },
                    {
                        key: 'backend-host',
                        label: 'Backend Host',
                        children: renderDiagnosticPropertyRows('BACKEND HOST'),
                    },
                    {
                        key: 'database',
                        label: 'Database',
                        children: renderDiagnosticPropertyRows('DATABASE'),
                    },
                    {
                        key: 'diagnostic-logs',
                        label: 'Diagnostic Logs',
                        children: renderDiagnosticLogs(),
                    },
                    {
                        key: 'ai-logs',
                        label: 'AI Logs',
                        children: renderAILogs(),
                    },
                ]}
            />
        );
    };

    const { data: diagnosticProperties, isLoading: isGetDiagnosticPropertiesLoading, isError: isGetDiagnosticPropertiesError } = useQuery<PropertyEntryResponseModel, Error>(
        ['maintenance-diagnostics', user?.currentToken],
        async () => {
            const url = `${process.env.REACT_APP_API_ROOT}${Maintenance.GET_PROPERTY_ENTRIES_BY_CURRENT_TOKEN}`;
            const requestData = { gpe_token: user?.currentToken ?? '' };

            return await AxiosUtil.get<PropertyEntryResponseModel, { gpe_token: string }>(url, requestData);
        },
        {
            enabled: diagnosticInformationVisible && canViewDiagnosticInformation && !!user?.currentToken,
        }
    );

    const {
        data: diagnosticLogs,
        isLoading: isGetDiagnosticLogsLoading,
        isError: isGetDiagnosticLogsError,
        refetch: refetchDiagnosticLogs,
    } = useQuery<NLogEntryModelResponse, Error>(
        ['maintenance-nlog', user?.currentToken, appliedDiagnosticLogLevel, appliedDiagnosticLogRowLimit],
        async () => {
            const url = `${process.env.REACT_APP_API_ROOT}${Maintenance.GET_NLOG_ENTRIES_BY_CURRENT_TOKEN}`;
            const requestData = {
                gne_token: user?.currentToken ?? '',
                gne_count: appliedDiagnosticLogRowLimit,
                gne_logType: appliedDiagnosticLogLevel,
            };

            return await AxiosUtil.get<NLogEntryModelResponse, { gne_token: string; gne_count: string; gne_logType: string }>(url, requestData);
        },
        {
            enabled: diagnosticInformationVisible && canViewDiagnosticInformation && !!user?.currentToken,
        }
    );

    const {
        data: aiLogs,
        isLoading: isGetAILogsLoading,
        isError: isGetAILogsError,
        refetch: refetchAILogs,
    } = useQuery<NLogEntryModelResponse, Error>(
        ['maintenance-ailog', user?.currentToken, appliedAILogLevel, appliedAILogRowLimit],
        async () => {
            const url = `${process.env.REACT_APP_API_ROOT}${Maintenance.GET_AILOG_ENTRIES_BY_CURRENT_TOKEN}`;
            const requestData = {
                gae_token: user?.currentToken ?? '',
                gae_count: appliedAILogRowLimit,
                gae_logType: appliedAILogLevel,
            };

            return await AxiosUtil.get<NLogEntryModelResponse, { gae_token: string; gae_count: string; gae_logType: string }>(url, requestData);
        },
        {
            enabled: diagnosticInformationVisible && canViewDiagnosticInformation && !!user?.currentToken,
        }
    );

    const handleOpenDiagnosticInformation = () => {
        setDiagnosticInformationVisible(true);
    };

    const handleCloseDiagnosticInformation = () => {
        setDiagnosticInformationVisible(false);
    };

    const buildUserName = () => {
        return (
                <>
                    {user?.firstName} {user?.lastName} {(user?.runningUser.firstName !== user?.firstName) || (user?.runningUser.lastName !== user?.lastName) ?
                        "(running as " + ((user?.runningUser ? user?.runningUser.firstName : "") + " " + (user?.runningUser ? user?.runningUser?.lastName : "")) + ")" : ""}
                </>
            );
    };

    const handleApplyImpersonate = () => {
        const impersonatableUserID = selectedImpersonatable || user?.runningUser?.id.toString() || user?.id.toString() || '';

        if (!impersonatableUserID) {
            return;
        }

        onImpersonateSelected(user?.currentToken ?? '', impersonatableUserID);
        setAccountImpersonateUserVisible(false);
    }

    const handleRevertImpersonate = () => {
        setSelectedImpersonatable(user?.id.toString() ?? '');
        onImpersonateSelected(user?.currentToken ?? '', user?.id.toString() ?? '');
        setAccountImpersonateUserVisible(false);
    }

    const headerMenuItems: MenuProps['items'] = [
        ...(canManageUsers(user)
            ?
            [
                {
                    key: 'administration',
                    label: (
                        <h6 className="m-0 d-flex align-items-center">
                            <SettingsIcon />&nbsp;<label><i>Administration</i></label>
                        </h6>
                    ),
                    children: [
                        ...(canManageLinkages(user)
                            ?
                            [
                                {
                                    key: 'adminUsers',
                                    label: <label>User Linkages</label>,
                                    onClick: () => nav('/adminUsers'),
                                },
                                {
                                    key: 'adminReports',
                                    label: <label>Report Linkages</label>,
                                    onClick: () => nav('/adminReports'),
                                },
                                {
                                    key: 'adminTenants',
                                    label: <label>Tenant Linkages</label>,
                                    onClick: () => nav('/adminTenants'),
                                },
                            ]
                            :
                            []
                        ),
                        ...(canManageTechAdministration(user)
                            ?
                            [
                                ...(canManageLinkages(user)
                                    ?
                                    [
                                        {
                                            key: 'administration-divider-1',
                                            disabled: true,
                                            style: {
                                                height: 1,
                                                lineHeight: '1px',
                                                padding: '2px 2px',
                                                margin: '2px',
                                            },
                                            label: (
                                                <div
                                                    style={{
                                                        borderTop: '1px solid gray',
                                                        width: '100%',
                                                    }}
                                                />
                                            ),
                                        },
                                    ]
                                    :
                                    []
                                ),
                                {
                                    key: 'audCharts',
                                    label: <label>Manage Charts</label>,
                                    onClick: () => nav('/audCharts'),
                                },
                                {
                                    key: 'audDataSets',
                                    label: <label>Manage DataSets</label>,
                                    onClick: () => nav('/audDataSets'),
                                },
                                {
                                    key: 'audFTPServers',
                                    label: <label>Manage FTP Servers</label>,
                                    onClick: () => nav('/audFTPServers'),
                                },
                                {
                                    key: 'audReports',
                                    label: <label>Manage Reports</label>,
                                    onClick: () => nav('/audReports'),
                                },
                                {
                                    key: 'audTenants',
                                    label: <label>Manage Tenants</label>,
                                    onClick: () => nav('/audTenants'),
                                },
                            ]
                            :
                            []
                        ),
                        {
                            key: 'audUsers',
                            label: <label>Manage Users</label>,
                            onClick: () => nav('/audUsers'),
                        },
                    ],
                },
            ]
            :
            []
        ),
        {
            key: 'tools',
            label: (
                <h6 className="m-0 d-flex align-items-center">
                    <SettingsIcon />&nbsp;<label>Tools</label>
                </h6>
            ),
            children: [
                {
                    key: 'accountUserLogs',
                    label: <label>User Operations Log</label>,
                    onClick: () => setAccountUserLogsVisible(true),
                },
                ...(canImpersonateUser(user)
                    ?
                    [
                        {
                            key: 'accountImpersonateUser',
                            label: <label>User Impersonation</label>,
                            onClick: () => {
                                setSelectedImpersonatable(user?.runningUser?.id.toString() ?? user?.id.toString() ?? '');
                                setAccountImpersonateUserVisible(true);
                            },
                        },
                    ]
                    :
                    []
                ),
                ...(canUseBIMaintenance(user)
                    ?
                    [
                        {
                            key: 'tools-divider-bi-report-maintenance',
                            disabled: true,
                            style: {
                                height: 1,
                                lineHeight: '1px',
                                padding: '2px 2px',
                                margin: '2px',
                            },
                            label: (
                                <div
                                    style={{
                                        borderTop: '1px solid gray',
                                        width: '100%',
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'retainbiMaintenance',
                            label: <label>RetainBI Maintenance</label>,
                            onClick: () => nav('/retainBIMaintenance'),
                        },
                    ]
                    :
                    []
                ),
                ...(canViewDiagnosticInformation
                    ?
                    [
                        {
                            key: 'tools-divider-diagnostic-information',
                            disabled: true,
                            style: {
                                height: 1,
                                lineHeight: '1px',
                                padding: '2px 2px',
                                margin: '2px',
                            },
                            label: (
                                <div
                                    style={{
                                        borderTop: '1px solid gray',
                                        width: '100%',
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'diagnosticInformation',
                            label: <label>Diagnostic Information</label>,
                            onClick: () => handleOpenDiagnosticInformation(),
                        },
                    ]
                    :
                    []
                ),
            ],
        },
        ...(canUseAdHocReportBuilder(user)
            ?
            [
                {
                    key: 'reporting',
                    label: (
                        <h6 className="m-0 d-flex align-items-center">
                            <FileTextOutlinedIcon />&nbsp;<label>Reporting</label>
                        </h6>
                    ),
                    children: [
                        {
                            key: 'ahReportBuilder',
                            label: <label>Ad Hoc Report Builder</label>,
                            onClick: () => nav('/ahReportBuilder'),
                        },
                    ],
                },
            ]
            :
            []
        ),
        {
            key: 'profile',
            label: (
                <h6 className="m-0 d-flex align-items-center">
                    <UserIcon />
                    <div
                        style={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }}
                        className="header-username"
                    >
                        &nbsp;{buildUserName()}
                    </div>
                </h6>
            ),
            children: [
                {
                    key: '/',
                    icon: <LogoutIcon style={{ fontSize: '16px' }} />,
                    label: (
                        <span
                            onClick={async () => {
                                await logout('Logged Out By User.');
                                nav('/');
                            }}
                        >
                            Logout
                        </span>
                    ),
                },
            ],
        },
    ];

    return (
        <>
            <ServaHeader
                style={{
                    zIndex: 10,
                    height: 'var(--retainbi-header-height)',
                }}
                className="d-flex align-items-center justify-content-between serva2025theme-top-header "
            >
                <Tooltip title="Go Home">
                    <img
                        onClick={() => nav('/home')}
                        alt='serva retainbi logo'
                        style={{ width: 180, cursor: 'pointer', marginLeft: 16 }}
                        src={logo}
                    />
                </Tooltip>
                <Menu
                    mode="horizontal"
                    selectable={false}
                    className="d-flex align-items-center justify-content-end"
                    selectedKeys={[]}
                    style={{
                        height: 'var(--retainbi-header-height)',
                        flex: 'auto',
                        right: 0,
                        marginRight: 10,
                        flexBasis: '100%',
                        flexShrink: 2,
                        borderRadius: 'inherit'
                    }}
                    items={headerMenuItems}
                />
            </ServaHeader>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={accountUserLogsVisible}
                title={
                    <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                        <Text style={{height: '100%', width: '100%'}}>
                            <Title style={{backgroundColor: 'rgb(00, 55, 90)', fontStyle: 'italic', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{'User Operations Log'}</Title>
                        </Text>
                    </Space>
                }
                width={'65%'}
                onCancel={() => setAccountUserLogsVisible(false)}
            >
                <AccountUserLogs 
                    onCancel={() => setAccountUserLogsVisible(false)}
                />
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={accountImpersonateUserVisible}
                title={
                    <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                        <Text style={{height: '100%', width: '100%'}}>
                            <Title style={{backgroundColor: 'rgb(00, 55, 90)', fontStyle: 'italic', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{'User Impersonation'}</Title>
                        </Text>
                    </Space>
                }
                width={'45%'}
                onCancel={() => setAccountImpersonateUserVisible(false)}
            >
                <AccountImpersonateUser 
                    selectedImpersonatable={selectedImpersonatable}
                    onApply={() => handleApplyImpersonate()}
                    onCancel={() => setAccountImpersonateUserVisible(false)}
                    onRevert={() => handleRevertImpersonate()}
                    onSelect={setSelectedImpersonatable}
                />
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={diagnosticInformationVisible}
                title={
                    <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                        <Text style={{height: '100%', width: '100%'}}>
                            <Title style={{backgroundColor: 'rgb(00, 55, 90)', fontStyle: 'italic', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{'RetainBI Diagnostic Information'}</Title>
                        </Text>
                    </Space>
                }
                width={'800px'}
                style={{top: '48px'}}
                onCancel={() => handleCloseDiagnosticInformation()}
            >
                <div style={{padding: '8px 16px'}}>
                    <div style={{height: '540px', overflowY: 'hidden', paddingRight: '8px'}}>
                        {isGetDiagnosticPropertiesLoading ? (
                            <div style={{display: 'grid', gridTemplateColumns: '220px 1fr', gap: '8px 16px', alignItems: 'center'}}>
                                <Text strong={true}>Diagnostic Information</Text>
                                <Text>Loading...</Text>
                            </div>
                        ) : isGetDiagnosticPropertiesError ? (
                            <div style={{display: 'grid', gridTemplateColumns: '220px 1fr', gap: '8px 16px', alignItems: 'center'}}>
                                <Text strong={true}>Diagnostic Information</Text>
                                <Text style={{color: 'red'}}>Unable to load backend diagnostic information.</Text>
                            </div>
                        ) : (
                            renderDiagnosticTabs()
                        )}
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '16px'}}>
                        <Col span={5}>
                            <Button
                                block
                                style={{width: '100%'}}
                                type="default"
                                onClick={handleCloseDiagnosticInformation}
                            >
                                Close
                            </Button>
                        </Col>
                        <Col span={14}>
                        </Col>
                        <Col span={5}>
                            {
                                (diagnosticInformationActiveTab === 'diagnostic-logs' || diagnosticInformationActiveTab === 'ai-logs')
                                &&
                                <Button
                                    block
                                    icon={<RefreshIcon />}
                                    style={{width: '100%'}}
                                    type='primary'
                                    onClick={() => {
                                        if (diagnosticInformationActiveTab === 'ai-logs') {
                                            setAppliedAILogLevel(aiLogLevel);
                                            setAppliedAILogRowLimit(aiLogRowLimit);
                                            refetchAILogs();
                                        } else {
                                            setAppliedDiagnosticLogLevel(diagnosticLogLevel);
                                            setAppliedDiagnosticLogRowLimit(diagnosticLogRowLimit);
                                            refetchDiagnosticLogs();
                                        }
                                    }}
                                >
                                    Load Logs
                                </Button>
                            }
                        </Col>
                    </Row>
                </div>
            </ServaModal>
        </>
    );
};

export default AppHeader;
